import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Share2, ChevronLeft, ChevronRight, X, Play } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATE_STYLES = {
  romantic: { bg: 'from-[#FFF5F5] via-[#FFF0F0] to-[#FFE8EC]', accent: '#DDA7A5', textColor: '#5C3A3A', font: "font-['Cormorant_Garamond']" },
  warm: { bg: 'from-[#FFFAF5] via-[#FFF5EB] to-[#FFF0E0]', accent: '#F4D5C9', textColor: '#5C4A3A', font: "font-['Cormorant_Garamond']" },
  nostalgic: { bg: 'from-[#FAF7F2] via-[#F5F0E8] to-[#EDE6D8]', accent: '#C9B896', textColor: '#5C5340', font: "font-['Cormorant_Garamond']" },
  minimal: { bg: 'from-[#FAFAF7] to-[#F5F5F0]', accent: '#C5DB7B', textColor: '#2C2A29', font: "font-['DM_Sans']" },
  elegant: { bg: 'from-[#F8F5FF] via-[#F3EEFF] to-[#ECE5FF]', accent: '#C3B1E1', textColor: '#4A3A5C', font: "font-['Cormorant_Garamond']" },
  tribute: { bg: 'from-[#F5F5F5] via-[#EFEFEF] to-[#E8E8E8]', accent: '#6B655F', textColor: '#3A3A3A', font: "font-['Cormorant_Garamond']" },
};

export default function MemoryPageView() {
  const { pageId } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const [isRevealed, setIsRevealed] = useState(false);

  useEffect(() => {
    axios.get(`${API}/memory-pages/${pageId}`)
      .then(r => { setPage(r.data); setTimeout(() => setIsRevealed(true), 500); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [pageId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FAFAF7] to-[#F5F0E8] flex items-center justify-center">
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <Heart size={40} className="text-[#DDA7A5]" fill="#DDA7A5" />
        </motion.div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <p className="text-[#6B655F]">Memory page not found</p>
      </div>
    );
  }

  const style = TEMPLATE_STYLES[page.template] || TEMPLATE_STYLES.romantic;
  const assets = page.assets || [];
  const hasMedia = assets.length > 0;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.3, delayChildren: 0.5 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-b ${style.bg}`} data-testid="memory-page-view">
      {/* Ambient background elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full opacity-20" style={{ background: `radial-gradient(circle, ${style.accent}40 0%, transparent 70%)` }} />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-15" style={{ background: `radial-gradient(circle, ${style.accent}30 0%, transparent 70%)` }} />
      </div>

      <motion.div
        className="max-w-lg mx-auto px-6 py-10 md:py-12 relative z-10"
        variants={containerVariants}
        initial="hidden"
        animate={isRevealed ? "visible" : "hidden"}
      >
        {/* Opening Heart Animation */}
        <motion.div
          variants={itemVariants}
          className="text-center mb-6"
        >
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          >
            <Heart
              size={36}
              className="mx-auto mb-6"
              style={{ color: page.accent_color || style.accent }}
              fill={page.accent_color || style.accent}
            />
          </motion.div>
          <h1
            className={`${style.font} text-4xl md:text-5xl font-light tracking-tight leading-tight`}
            style={{ color: style.textColor }}
            data-testid="memory-title"
          >
            {page.title || 'A Memory For You'}
          </h1>
        </motion.div>

        {/* Message */}
        {page.message && (
          <motion.div variants={itemVariants} className="mb-6">
            <div className="relative px-4">
              <div className="absolute -left-2 top-0 w-1 h-full rounded-full" style={{ backgroundColor: `${page.accent_color || style.accent}40` }} />
              <p
                className={`${style.font} text-lg md:text-xl leading-relaxed italic`}
                style={{ color: `${style.textColor}CC` }}
                data-testid="memory-message"
              >
                {page.message}
              </p>
            </div>
          </motion.div>
        )}

        {/* Media Gallery */}
        {hasMedia && (
          <motion.div variants={itemVariants} className="mb-6">
            {page.layout_style === 'gallery' ? (
              <div className="grid grid-cols-2 gap-3">
                {assets.map((asset, i) => (
                  <motion.div
                    key={asset.asset_id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.8 + i * 0.15, duration: 0.6 }}
                    className={`rounded-2xl overflow-hidden shadow-lg cursor-pointer group ${i === 0 && assets.length > 2 ? 'col-span-2' : ''}`}
                    onClick={() => setLightboxIdx(i)}
                    data-testid={`media-${asset.asset_id}`}
                  >
                    {asset.content_type?.startsWith('video') ? (
                      <div className="relative">
                        <video src={`${process.env.REACT_APP_BACKEND_URL}${asset.url}`} className="w-full aspect-square object-cover" />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                          <Play size={32} className="text-white" fill="white" />
                        </div>
                      </div>
                    ) : (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${asset.url}`}
                        alt={asset.caption || ''}
                        className={`w-full ${i === 0 && assets.length > 2 ? 'aspect-[16/9]' : 'aspect-square'} object-cover group-hover:scale-105 transition-transform duration-700`}
                      />
                    )}
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="space-y-8">
                {assets.map((asset, i) => (
                  <motion.div
                    key={asset.asset_id}
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 + i * 0.2, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="cursor-pointer"
                    onClick={() => setLightboxIdx(i)}
                    data-testid={`media-${asset.asset_id}`}
                  >
                    {asset.content_type?.startsWith('video') ? (
                      <video
                        src={`${process.env.REACT_APP_BACKEND_URL}${asset.url}`}
                        controls
                        className="w-full rounded-2xl shadow-xl"
                      />
                    ) : (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${asset.url}`}
                        alt={asset.caption || ''}
                        className="w-full rounded-2xl shadow-xl object-cover hover:shadow-2xl transition-shadow duration-500"
                      />
                    )}
                    {asset.caption && (
                      <p className={`text-center text-sm mt-4 font-light italic`} style={{ color: `${style.textColor}99` }}>
                        {asset.caption}
                      </p>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Share & Footer */}
        <motion.div
          variants={itemVariants}
          className="text-center pt-12"
        >
          {/* Share Buttons */}
          <div className="flex justify-center gap-3 mb-8" data-testid="share-buttons">
            <button
              onClick={() => {
                const url = window.location.href;
                const text = `${page.title || 'A Memory For You'} - Made with Seikat`;
                window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition-all hover:scale-105"
              style={{ backgroundColor: `${style.accent}20`, color: style.textColor }}
              data-testid="share-whatsapp"
            >
              <Share2 size={14} /> WhatsApp
            </button>
            <button
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                alert('Link copied!');
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition-all hover:scale-105"
              style={{ backgroundColor: `${style.accent}15`, color: `${style.textColor}99` }}
              data-testid="share-copy"
            >
              <Share2 size={14} /> Copy Link
            </button>
          </div>
          <div className="w-12 h-[1px] mx-auto mb-6" style={{ backgroundColor: `${style.accent}40` }} />
          <p className="text-[11px] font-light tracking-widest uppercase" style={{ color: `${style.textColor}40` }}>
            Made with love by Seikat
          </p>
        </motion.div>
      </motion.div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIdx >= 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
            onClick={() => setLightboxIdx(-1)}
            data-testid="lightbox"
          >
            <button onClick={() => setLightboxIdx(-1)} className="absolute top-6 right-6 text-white/60 hover:text-white z-50">
              <X size={28} />
            </button>
            {assets.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); setLightboxIdx(prev => prev > 0 ? prev - 1 : assets.length - 1); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white z-50"
                >
                  <ChevronLeft size={36} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setLightboxIdx(prev => prev < assets.length - 1 ? prev + 1 : 0); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white z-50"
                >
                  <ChevronRight size={36} />
                </button>
              </>
            )}
            <motion.div
              key={lightboxIdx}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-4xl max-h-[85vh] mx-4"
              onClick={e => e.stopPropagation()}
            >
              {assets[lightboxIdx]?.content_type?.startsWith('video') ? (
                <video src={`${process.env.REACT_APP_BACKEND_URL}${assets[lightboxIdx].url}`} controls autoPlay className="max-w-full max-h-[85vh] rounded-xl" />
              ) : (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${assets[lightboxIdx]?.url}`} alt="" className="max-w-full max-h-[85vh] rounded-xl object-contain" />
              )}
              {assets[lightboxIdx]?.caption && (
                <p className="text-center text-white/70 text-sm mt-4 font-light">{assets[lightboxIdx].caption}</p>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
